'use client'

import { isEmpty, isEqual, omit } from 'lodash-es'
import { useEffect, useRef } from 'react'

import {
  useSearchFilterStore,
  TSearchFilter,
  defaultFilters,
  PYEONG_SIZE_MAP,
  POPULAR_OPTION_MAP,
  BASIC_OPTION_MAP,
  ADDITIONAL_OPTION_MAP,
} from '@/entities/map'
import { ROOM_COUNT_MAP, ROOM_DISCOUNT_MAP } from '@/entities/map'
import { PROPERTY_TYPE_MAP } from '@/entities/room'
import { useAirbridgeContext } from '@/shared/config'

const AIRBRIDGE_EXCLUDED_FILTERS = [
  'minUsingFee',
  'maxUsingFee',
  'floors',
] as const

const defaultState = omit(defaultFilters, AIRBRIDGE_EXCLUDED_FILTERS)

const formatLabel = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return ''

  const formatDate = (date: string) => date.replace(/-/g, '.')

  return `${formatDate(startDate)} ~ ${formatDate(endDate)}`
}

const toLabels = (
  values: string[],
  labelMap: Record<string, string>,
  separator = ', ',
) => values.map(v => labelMap[v]).join(separator)

type LastSentEvent = {
  keyword: string
  filters: Omit<TSearchFilter, (typeof AIRBRIDGE_EXCLUDED_FILTERS)[number]>
}

export const AirbridgeEventSender = () => {
  const { isInitialized, sendEvent } = useAirbridgeContext()
  const filters = useSearchFilterStore(state => state.filters)
  const keyword = useSearchFilterStore(state => state.keyword)
  const lastSentEventRef = useRef<LastSentEvent>({
    keyword: '',
    filters: defaultState,
  })

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    const airbridgeFilters = omit(filters, AIRBRIDGE_EXCLUDED_FILTERS)

    const lastSentEvent = lastSentEventRef.current
    const isKeywordChanged =
      lastSentEvent.keyword !== keyword && !isEmpty(keyword)
    const isFiltersChanged =
      !isEqual(lastSentEvent.filters, airbridgeFilters) &&
      !isEqual(defaultState, airbridgeFilters)

    if (!isKeywordChanged && !isFiltersChanged) {
      return
    }

    lastSentEventRef.current = { keyword, filters: airbridgeFilters }

    const {
      startDate,
      endDate,
      roomCounts,
      pyeongSizes,
      popularOptions,
      basicOptions,
      additionalOptions,
      roomDiscounts,
      propertyTypes,
    } = airbridgeFilters

    const params = {
      action: keyword,
      label: formatLabel(startDate, endDate),
      semanticAttributes: {
        transactionPairedEventCategory: toLabels(roomCounts, ROOM_COUNT_MAP),
        transactionType: toLabels(pyeongSizes, PYEONG_SIZE_MAP),
        description: toLabels(
          [...popularOptions, ...basicOptions, ...additionalOptions],
          {
            ...POPULAR_OPTION_MAP,
            ...BASIC_OPTION_MAP,
            ...ADDITIONAL_OPTION_MAP,
          },
        ),
        type: toLabels(roomDiscounts, ROOM_DISCOUNT_MAP),
        products: [
          {
            categoryName: toLabels(propertyTypes, PROPERTY_TYPE_MAP),
          },
        ],
      },
    }

    sendEvent({
      event: 'airbridge.ecommerce.searchResults.viewed',
      params,
    })
  }, [isInitialized, filters, keyword, sendEvent])

  return null
}
